<template>
  <aside class="col-12 col-md-3 left-sidebar wp-sidebar">
    <div class="the-hub-sidebar__header">
      <nuxt-link to="/the-hub" class="hub-logo" aria-label="The Hub">
        <span class="hub-logo__the">the</span>
        <span class="hub-logo__hub">Hub</span>
      </nuxt-link>

      <h3 class="block-title">
        <button
          type="button"
          class="the-hub-categories__heading"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          aria-controls="the-hub-categories"
          @click="mobileOpen = !mobileOpen"
        >
          <span>Categories</span>
          <span class="the-hub-categories__toggle the-hub-categories__heading-icon" aria-hidden="true">{{ mobileOpen ? '−' : '+' }}</span>
        </button>
      </h3>
    </div>
    <ul id="the-hub-categories" class="the-hub-categories" :class="{ 'is-open': mobileOpen }">
      <li v-for="category in categories" :key="category.id">
        <div class="the-hub-categories__row">
          <a :href="categoryUrl(category)" :class="{ 'is-active': category.slug === activeSlug }">{{ category.name }}</a>
          <button
            v-if="category.children.length"
            type="button"
            class="the-hub-categories__toggle"
            :aria-expanded="isOpen(category) ? 'true' : 'false'"
            :aria-controls="`the-hub-subcategories-${category.id}`"
            :aria-label="`${isOpen(category) ? 'Collapse' : 'Expand'} ${category.name}`"
            @click="toggle(category)"
          >
            {{ isOpen(category) ? '−' : '+' }}
          </button>
        </div>
        <ul v-if="category.children.length" v-show="isOpen(category)" :id="`the-hub-subcategories-${category.id}`" class="the-hub-categories__children">
          <li v-for="child in category.children" :key="child.id">
            <a :href="categoryUrl(child)" :class="{ 'is-active': child.slug === activeSlug }">{{ child.name }}</a>
          </li>
        </ul>
      </li>
    </ul>
  </aside>
</template>

<script>
import { getTopLevelCategories, getChildCategories, categoryPath } from '~/lib/data/the-hub-categories'

// Static data: build the tree once, not per render.
const CATEGORY_TREE = getTopLevelCategories().map(category => ({ ...category, children: getChildCategories(category) }))

export default {
  name: 'TheHubSidebar',
  // SSR-cached per active category (the only input that changes the markup).
  serverCacheKey: props => `TheHubSidebar::${props.activeSlug}`,
  props: {
    activeSlug: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    // Branches start open only when they contain the active category, so the markup depends on activeSlug alone (cache-safe).
    const open = {}
    CATEGORY_TREE.forEach(category => {
      open[category.id] = category.children.some(child => child.slug === this.activeSlug) || category.slug === this.activeSlug
    })
    // mobileOpen starts false so the SSR-cached markup is identical for every request.
    return { categories: CATEGORY_TREE, open, mobileOpen: false }
  },
  methods: {
    isOpen(category) {
      return !!this.open[category.id]
    },
    toggle(category) {
      this.$set(this.open, category.id, !this.open[category.id])
    },
    categoryUrl(category) {
      return `${this.$config.baseURL}${categoryPath(category)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.hub-logo {
  text-decoration: none;
  line-height: 0.9;
  &__the {
    display: inline-block;
    background: #e1000b;
    color: #fff;
    font-weight: 700;
    font-style: italic;
    padding: 2px 8px;
    border-radius: 4px;
    transform: rotate(-4deg);
  }
  &__hub {
    display: block;
    color: #363636;
    font-size: 64px;
    font-weight: 900;
    font-style: italic;
  }
}
.the-hub-categories {
  list-style: none;
  padding: 0;
  li {
    padding: 6px 0;
    border-bottom: 1px solid #e9e9e9;
  }
  a {
    color: #363636;
    text-decoration: none;
    &:hover,
    &.is-active {
      color: #e1000b;
    }
    &.is-active {
      font-weight: 700;
    }
  }
  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__toggle {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    line-height: 20px;
    padding: 0;
    border: 1px solid #dadada;
    border-radius: 3px;
    background: #fff;
    color: #636467;
    cursor: pointer;
    &:hover {
      background: #e1000b;
      border-color: #e1000b;
      color: #fff;
    }
  }
  &__children {
    margin-top: 6px;
    padding-left: 15px;
    li {
      border-bottom: 0;
      padding: 3px 0;
    }
  }
  &__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    color: #363636;
    font: inherit;
    font-weight: 700;
    text-align: left;
    cursor: pointer;
  }
  &__heading-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
}

// Mobile: compact Hub logo on the same row as the "Categories" toggle; list collapsed by default. Desktop: always shown.
@media (max-width: #{$screen-sm-min - 1}) {
  .the-hub-sidebar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 10px 0;
    padding: 10px 12px;
    border: 1px solid #dadada;
    border-radius: 4px;
    background: #f2f2f2;
  }
  .hub-logo {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 0 35px 0 0;
    line-height: 1;
    &__the {
      font-size: 9px;
      line-height: 1.2;
      padding: 0 4px;
    }
    &__hub {
      font-size: 24px;
      line-height: 1;
    }
  }
  // Beats the global `.left-sidebar .block-title` / `.left-sidebar h3` margins from the-hub-style.css and Bootstrap's h3 margin.
  .left-sidebar .the-hub-sidebar__header .block-title {
    flex: 1 1 auto;
    margin: 0;
    padding: 0;
    line-height: 1;
  }
  .the-hub-categories__heading {
    line-height: 1;
  }
  .the-hub-categories__heading-icon {
    border-color: #636467;
    color: #363636;
  }
  .the-hub-categories {
    display: none;
    &.is-open {
      display: block;
    }
  }
}
@media (min-width: $screen-sm-min) {
  .the-hub-categories__heading {
    cursor: default;
    pointer-events: none;
  }
  .the-hub-categories__heading-icon {
    display: none;
  }
}
</style>
